import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight, CheckCircle2, Circle, Info } from 'lucide-react';
import type {
  OverviewResponse,
  PermissionCode,
  RequisitionSummary,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatCents, formatTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { canOpen } from '@/navigation';

/**
 * Visão Geral.
 *
 * Responde, nesta ordem, ao que o Diretor pergunta quando abre o sistema:
 *
 * 1. Como estão os números que movem o dia — contas vencidas, dinheiro a
 *    entrar, compras paradas;
 * 2. o que exige ação agora;
 * 3. o que aconteceu desde a última vez.
 *
 * Os indicadores saem das MESMAS rotas que alimentam cada tela (contas a
 * pagar e a receber, requisições): o número daqui é o número de lá,
 * com o mesmo filtro. Cada um só aparece para quem pode abrir a tela dele —
 * indicador que leva a "sem permissão" é pior que indicador nenhum.
 *
 * "Itens abaixo do mínimo" NÃO está aqui, e é de propósito: o `/items`
 * pagina antes de filtrar o mínimo (o saldo só existe depois de somar os
 * lotes), então o total que ele devolve é o da página, não o do estoque.
 * Um "0 · estoque em dia" que pode ser mentira é pior que nenhum número.
 * Volta quando a API contar o filtro inteiro.
 *
 * Sem variação percentual: nada disso guarda série histórica, e "+25%"
 * inventado é decisão tomada em cima de nada.
 *
 * O que já estava aqui e continua: a lista de atenção do `/overview`, a
 * trilha recente, e os contadores de cadastro — que desceram para um bloco
 * lateral, porque são base do sistema e não o pulso do parque.
 */

/** Cada contador de cadastro leva para a tela onde ele pode ser mexido. */
const METRIC_LINK: Record<string, { to: string; permission?: PermissionCode | PermissionCode[] }> =
  {
    pessoas: { to: '/cadastros/pessoas', permission: 'pessoas:ver' },
    fornecedores: {
      to: '/financeiro/fornecedores',
      permission: ['pessoas:ver', 'lancamentos:ver'],
    },
    'centros-custo': { to: '/financeiro/centros-custo', permission: 'centros-custo:ver' },
    'plano-contas': { to: '/financeiro/plano-contas', permission: 'plano-contas:ver' },
    usuarios: { to: '/configuracoes/usuarios', permission: 'usuarios:ver' },
    colaboradores: { to: '/rh/colaboradores', permission: 'colaboradores:ver' },
  };

/**
 * Rotas como `string`: a árvore do painel é montada em runtime pelo
 * `guardedRoute`, e as rotas aninhadas não chegam ao tipo do `Link`.
 */
const ROTA = {
  auditoria: '/configuracoes/auditoria',
  agenda: '/financeiro/agenda',
  pagar: '/financeiro/contas-pagar',
  receber: '/financeiro/contas-receber',
  requisicoes: '/compras/requisicoes',
} satisfies Record<string, string>;

const FUSO = 'America/Sao_Paulo';

/** Hoje por extenso, no fuso do parque — o mesmo "hoje" do resto do sistema. */
function hojePorExtenso(): string {
  const texto = new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO,
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date());
  // Maiúscula SÓ na primeira letra. O `text-transform: capitalize` do CSS
  // escrevia "Sexta-Feira, 18 De Setembro" — ele não sabe que "de" é preposição.
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/** "Bom dia" pela hora do PARQUE, não do navegador de quem abre. */
function saudacao(): string {
  const hora = Number(
    new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, hour: '2-digit', hourCycle: 'h23' }).format(
      new Date(),
    ),
  );
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}

type EntrySummary = { summary: { totalCents: number; openCents: number; overdueCents: number } };

interface Kpi {
  key: string;
  label: string;
  /** `null` enquanto carrega. */
  value: string | null;
  /** A chamada falhou: mostra "—" e diz isso, em vez de carregar para sempre. */
  falhou?: boolean;
  note?: string;
  tone?: 'warn' | 'bad';
  to: string;
}

export function OverviewPage() {
  const { user, can } = useAuth();

  const query = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<OverviewResponse>('/overview'),
    refetchInterval: 60_000,
  });

  // Indicadores. `perPage: 1` porque só o resumo interessa — a lista em si
  // é da tela de contas.
  const podeFinanceiro = can('lancamentos:ver');
  const pagar = useQuery({
    queryKey: ['overview', 'a-pagar'],
    queryFn: () =>
      api.get<EntrySummary>('/financial-entries', { type: 'PAYABLE', page: 1, perPage: 1 }),
    enabled: podeFinanceiro,
  });
  const receber = useQuery({
    queryKey: ['overview', 'a-receber'],
    queryFn: () =>
      api.get<EntrySummary>('/financial-entries', { type: 'RECEIVABLE', page: 1, perPage: 1 }),
    enabled: podeFinanceiro,
  });
  const requisicoes = useQuery({
    queryKey: ['overview', 'requisicoes'],
    queryFn: () => api.get<RequisitionSummary>('/purchase-requisitions/summary'),
    enabled: can('requisicoes:ver'),
  });

  const kpis: Kpi[] = [];
  if (podeFinanceiro) {
    const s = pagar.data?.summary;
    kpis.push({
      key: 'a-pagar',
      label: 'A pagar vencido',
      value: s ? formatCents(s.overdueCents) : null,
      note: s ? `${formatCents(s.openCents)} em aberto` : undefined,
      tone: s && s.overdueCents > 0 ? 'bad' : undefined,
      falhou: pagar.isError,
      to: ROTA.pagar,
    });
    const r = receber.data?.summary;
    kpis.push({
      key: 'a-receber',
      label: 'A receber em aberto',
      value: r ? formatCents(r.openCents) : null,
      note: r
        ? r.overdueCents > 0
          ? `${formatCents(r.overdueCents)} vencido`
          : 'nada vencido'
        : undefined,
      falhou: receber.isError,
      to: ROTA.receber,
    });
  }
  if (can('requisicoes:ver')) {
    const q = requisicoes.data;
    kpis.push({
      key: 'requisicoes',
      label: 'Requisições para aprovar',
      value: q ? String(q.waitingApproval) : null,
      note: q ? `${q.approvedPending} aprovada(s) sem cotação` : undefined,
      tone: q && q.waitingApproval > 0 ? 'warn' : undefined,
      falhou: requisicoes.isError,
      to: ROTA.requisicoes,
    });
  }

  const data = query.data;
  const numero = (key: string) => Number(data?.metrics.find((m) => m.key === key)?.value ?? '0');

  /**
   * Configuração inicial: cada linha é uma dependência real do dia a dia.
   * Sem centro de custo não há como saber onde se gastou; sem plano de contas o
   * lançamento não tem onde entrar. Só aparece enquanto FALTA algo.
   */
  // Os rótulos NÃO repetem os dos contadores de cadastro de propósito: com o
  // mesmo texto em dois lugares, um `getByText('Centros de custo')` casa os
  // dois e o teste quebra por ambiguidade.
  const checklist = [
    { label: 'Cadastro de pessoas iniciado', ok: numero('pessoas') > 0 },
    { label: 'Ao menos um fornecedor', ok: numero('fornecedores') > 0 },
    { label: 'Árvore de custos montada', ok: numero('centros-custo') > 0 },
    { label: 'Plano de contas montado', ok: numero('plano-contas') > 0 },
    { label: 'Operadores com acesso', ok: numero('usuarios') > 0 },
  ];
  const prontos = checklist.filter((item) => item.ok).length;
  const tudoPronto = prontos === checklist.length;

  const primeiroNome = user?.name.trim().split(/\s+/)[0] ?? '';

  return (
    <>
      <WorkspaceHeader
        title={primeiroNome ? `${saudacao()}, ${primeiroNome}` : saudacao()}
        description={hojePorExtenso()}
        actions={
          <>
            {canOpen(can, 'requisicoes:ver') && (
              <Link to={ROTA.requisicoes} className="btn btn--outline">
                Requisições
              </Link>
            )}
            {podeFinanceiro && (
              <Link to={ROTA.agenda} className="btn btn--primary">
                Agenda de vencimentos
              </Link>
            )}
          </>
        }
      />

      <div className="workspace__body">
        {kpis.length > 0 && (
          <section className="kpis" aria-label="Indicadores">
            {kpis.map((kpi) => (
              <Link key={kpi.key} to={kpi.to} className="kpi kpi--link">
                <span className="kpi__label">{kpi.label}</span>
                {kpi.falhou ? (
                  <>
                    <strong className="kpi__value">—</strong>
                    <span className="kpi__note">Não carregou agora — abra a tela para ver.</span>
                  </>
                ) : kpi.value === null ? (
                  <span className="skeleton kpi__skeleton" aria-label="Carregando" />
                ) : (
                  <strong className={`kpi__value${kpi.tone ? ` kpi__value--${kpi.tone}` : ''}`}>
                    {kpi.value}
                  </strong>
                )}
                {!kpi.falhou && kpi.note && <span className="kpi__note">{kpi.note}</span>}
                <ArrowRight className="kpi__go" size={14} aria-hidden />
              </Link>
            ))}
          </section>
        )}

        {query.isPending && <LoadingState />}

        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {data && (
          <div className="vg">
            <div className="vg__main">
              <section className="panel">
                <div className="panel__head">
                  <h2 className="panel__title">Pendências</h2>
                  {data.attention.length > 0 && (
                    <span className="panel__count">{data.attention.length}</span>
                  )}
                </div>
                {data.attention.length === 0 ? (
                  <p className="vg__tudo-certo">
                    <CheckCircle2 size={16} color={T.green} aria-hidden />
                    Nada exige sua atenção agora.
                  </p>
                ) : (
                  <ul className="pendencias" aria-label="Itens que exigem atenção">
                    {data.attention.map((item) => {
                      const Icon = item.kind === 'info' ? Info : AlertTriangle;
                      return (
                        <li key={item.id} className={`pendencia pendencia--${item.kind}`}>
                          <Icon size={16} className="pendencia__icon" aria-hidden />
                          <span className="pendencia__text">
                            {item.text}
                            {item.count !== null && item.count > 1 ? ` (${item.count})` : ''}
                          </span>
                          <Link to={item.href} className="panel__link">
                            {item.action} <ArrowRight size={12} aria-hidden />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section className="panel">
                <div className="panel__head">
                  <h2 className="panel__title">Movimentação recente</h2>
                  {canOpen(can, 'auditoria:ver') && (
                    <Link to={ROTA.auditoria} className="panel__link">
                      Ver todas <ArrowRight size={12} aria-hidden />
                    </Link>
                  )}
                </div>
                {data.activity.length === 0 ? (
                  <p className="panel__empty">Ainda não há movimentação registrada.</p>
                ) : (
                  <div className="activities">
                    {data.activity.map((entry) => (
                      <div key={entry.id} className="activity">
                        <span className="activity__time">{formatTime(entry.at)}</span>
                        <span className="activity__text">{entry.text}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>

            <aside className="vg__side">
              {/* Configuração inicial: só ocupa a tela enquanto falta algo. */}
              {!tudoPronto && (
                <section className="panel">
                  <div className="panel__head">
                    <h2 className="panel__title">Configuração inicial</h2>
                    <span className="panel__count">
                      {prontos} de {checklist.length}
                    </span>
                  </div>
                  <ul className="checklist">
                    {checklist.map((item) => (
                      <li key={item.label} className={item.ok ? 'is-ok' : ''}>
                        {item.ok ? (
                          <CheckCircle2 size={15} color={T.green} aria-hidden />
                        ) : (
                          <Circle size={15} color={T.inkFaint} aria-hidden />
                        )}
                        {item.label}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="panel">
                <div className="panel__head">
                  <h2 className="panel__title">Cadastros</h2>
                </div>
                <ul className="cadastros">
                  {data.metrics.map((metric) => {
                    const destino = METRIC_LINK[metric.key];
                    const abre = destino && canOpen(can, destino.permission);
                    const corpo = (
                      <>
                        <span className="cadastro__label">
                          {metric.label}
                          {metric.note && <span className="cadastro__note">{metric.note}</span>}
                        </span>
                        <strong className="cadastro__value">{metric.value}</strong>
                      </>
                    );
                    return (
                      <li key={metric.key}>
                        {abre ? (
                          <Link to={destino.to} className="cadastro cadastro--link">
                            {corpo}
                          </Link>
                        ) : (
                          <div className="cadastro">{corpo}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </aside>
          </div>
        )}
      </div>
    </>
  );
}
